import React, { useState, useMemo } from "react"
import HeaderSection from "../../components/header-section"
import Sidebar from "../../features/sidebar/components/sidebar"
import {
  useGetAdminOrganizationReportsQuery,
  useLazyGetOrgReportDetailsQuery,
  useSetManuallyPaidStatusMutation,
  OrgReportDetailsData,
  OrgReportUser,
  OrgReportUserSimulation
} from "../../app/services/meseeknaApiV2"
import { HiOutlineOfficeBuilding } from "react-icons/hi"
import { FiUsers, FiActivity, FiCheckCircle, FiSearch, FiChevronDown, FiChevronRight, FiX, FiAward, FiDollarSign } from "react-icons/fi"
import { MdOutlineGroups } from "react-icons/md"

// Icon wrapper components for TypeScript compatibility
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const BuildingIcon = (props: { size?: number; className?: string }): JSX.Element => <HiOutlineOfficeBuilding {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const UsersIcon = (props: { size?: number; className?: string }): JSX.Element => <FiUsers {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ActivityIcon = (props: { size?: number; className?: string }): JSX.Element => <FiActivity {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CheckIcon = (props: { size?: number; className?: string }): JSX.Element => <FiCheckCircle {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const SearchIcon = (props: { size?: number; className?: string }): JSX.Element => <FiSearch {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const GroupsIcon = (props: { size?: number; className?: string }): JSX.Element => <MdOutlineGroups {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronDownIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronDown {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const ChevronRightIcon = (props: { size?: number; className?: string }): JSX.Element => <FiChevronRight {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const CloseIcon = (props: { size?: number; className?: string }): JSX.Element => <FiX {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const AwardIcon = (props: { size?: number; className?: string }): JSX.Element => <FiAward {...props} />
// @ts-expect-error - React icons return ReactNode which is valid for JSX
const DollarIcon = (props: { size?: number; className?: string }): JSX.Element => <FiDollarSign {...props} />

// User Simulation Row component
const SimulationRow: React.FC<{ sim: OrgReportUserSimulation }> = ({ sim }) => {
  const [showScores, setShowScores] = useState(false)

  const getStatusBadgeForSim = (status: string) => {
    if (status === "completed") {
      return <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">Completed</span>
    }
    if (status === "in_progress") {
      return <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">In Progress</span>
    }
    return <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">Not Started</span>
  }

  return (
    <div className="border-l-2 border-gray-200 pl-3 py-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {sim.scores.length > 0 && (
            <button
              onClick={() => setShowScores(!showScores)}
              className="p-0.5 hover:bg-gray-100 rounded"
            >
              {showScores ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
            </button>
          )}
          <span className="text-sm font-medium text-gray-700">
            {sim.testTitle || sim.testShortName || `Test #${sim.testId}`}
          </span>
          {getStatusBadgeForSim(sim.status)}
        </div>
        <div className="text-xs text-gray-500">
          {sim.dateUserEnd
            ? `Completed ${new Date(sim.dateUserEnd).toLocaleDateString()}`
            : sim.dateUserStart
            ? `Started ${new Date(sim.dateUserStart).toLocaleDateString()}`
            : sim.dateAssigned
            ? `Assigned ${new Date(sim.dateAssigned).toLocaleDateString()}`
            : ""}
        </div>
      </div>
      {showScores && sim.scores.length > 0 && (
        <div className="mt-2 ml-6 bg-gray-50 rounded-lg p-3">
          <div className="text-xs font-medium text-gray-500 mb-2">Measure Scores</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {sim.scores.map((score) => (
              <div key={score.measureId} className="flex items-center justify-between bg-white rounded px-2 py-1 text-xs">
                <span className="text-gray-600 truncate" title={score.measureName}>
                  {score.measureCode || score.measureName.slice(0, 15)}
                </span>
                <span className="font-semibold text-gray-900 ml-2">{score.score.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// User Card component
const UserCard: React.FC<{ user: OrgReportUser }> = ({ user }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border border-gray-200 rounded-lg mb-2 bg-white">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
            <UsersIcon size={14} className="text-purple-600" />
          </div>
          <div className="text-left">
            <div className="font-medium text-gray-900">
              {user.firstName || user.lastName
                ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                : user.email}
            </div>
            <div className="text-xs text-gray-500">{user.email}</div>
          </div>
          {user.role && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">{user.role}</span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{user.summary.completed}</span>
              <span className="text-gray-400">/{user.summary.totalAssigned}</span>
              <span className="text-xs text-gray-500 ml-1">completed</span>
            </div>
            {user.summary.averageScore !== null && (
              <div className="text-xs text-gray-500">
                Avg Score: <span className="font-medium text-gray-700">{user.summary.averageScore}</span>
              </div>
            )}
          </div>
          {expanded ? <ChevronDownIcon size={16} className="text-gray-400" /> : <ChevronRightIcon size={16} className="text-gray-400" />}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {user.simulations.length === 0 ? (
            <div className="py-4 text-sm text-gray-500 text-center">No simulations assigned</div>
          ) : (
            <div className="mt-3 space-y-2">
              {user.simulations.map((sim) => (
                <SimulationRow key={sim.assignmentId} sim={sim} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Organization Details Panel
const OrgDetailsPanel: React.FC<{
  data: OrgReportDetailsData
  onClose: () => void
  isLoading: boolean
}> = ({ data, onClose, isLoading }) => {
  const [userSearch, setUserSearch] = useState("")

  const filteredUsers = useMemo(() => {
    if (!userSearch) return data.users
    const query = userSearch.toLowerCase()
    return data.users.filter(
      (user) =>
        user.email.toLowerCase().includes(query) ||
        (user.firstName && user.firstName.toLowerCase().includes(query)) ||
        (user.lastName && user.lastName.toLowerCase().includes(query))
    )
  }, [data.users, userSearch])

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading organization details...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end md:items-center justify-center">
      <div className="bg-white w-full md:max-w-4xl md:rounded-lg md:max-h-[90vh] max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BuildingIcon size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{data.organization.organizationName}</h2>
              <p className="text-sm text-gray-500">Organization Details</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <CloseIcon size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Summary Stats */}
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{data.summary.totalUsers}</div>
              <div className="text-xs text-gray-500">Users</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{data.summary.totalSimulationsAssigned}</div>
              <div className="text-xs text-gray-500">Assigned</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{data.summary.totalSimulationsCompleted}</div>
              <div className="text-xs text-gray-500">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{data.summary.totalSimulationsInProgress}</div>
              <div className="text-xs text-gray-500">In Progress</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <AwardIcon size={16} className="text-amber-500" />
                <span className="text-2xl font-bold text-gray-900">
                  {data.summary.overallAverageScore !== null ? data.summary.overallAverageScore : "N/A"}
                </span>
              </div>
              <div className="text-xs text-gray-500">Avg Score</div>
            </div>
          </div>
        </div>

        {/* Search Users */}
        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Users List */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredUsers.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              {userSearch ? "No users found matching your search." : "No users in this organization."}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map((user) => (
                <UserCard key={user.id} user={user} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type ArchiveFilter = "active" | "archived" | "all"

const OrgReportsPage: React.FC = () => {
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>("active")

  // Query params based on archive filter
  const queryParams = archiveFilter === "active"
    ? {}
    : archiveFilter === "archived"
    ? { archivedOnly: true }
    : { includeArchived: true }

  const { data, isLoading, refetch } = useGetAdminOrganizationReportsQuery(queryParams)
  const [getOrgDetails, { data: orgDetailsData, isLoading: orgDetailsLoading }] = useLazyGetOrgReportDetailsQuery()
  const [setManuallyPaidStatus, { isLoading: isSettingManuallyPaid }] = useSetManuallyPaidStatusMutation()
  const [searchQuery, setSearchQuery] = useState("")
  const [sortField, setSortField] = useState<"organizationName" | "userCount" | "completedSimulations">("organizationName")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)

  const reports = data?.data || []

  // Refetch when archive filter changes
  React.useEffect(() => {
    refetch()
  }, [archiveFilter, refetch])

  const filteredAndSortedReports = useMemo(() => {
    const filtered = reports.filter((org) =>
      org.organizationName.toLowerCase().includes(searchQuery.toLowerCase())
    )

    return [...filtered].sort((a, b) => {
      const aVal = a[sortField]
      const bVal = b[sortField]
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      }
      return sortDirection === "asc"
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number)
    })
  }, [reports, searchQuery, sortField, sortDirection])

  const handleSort = (field: typeof sortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const handleOrgClick = async (orgId: number) => {
    setSelectedOrgId(orgId)
    getOrgDetails(orgId)
  }

  const handleCloseDetails = () => {
    setSelectedOrgId(null)
  }

  const getStatusBadge = (status: string | null, isManuallyPaid: boolean) => {
    // If manually paid, show that badge prominently
    if (isManuallyPaid) {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800 flex items-center gap-1">
          <DollarIcon size={12} />
          Manual
        </span>
      )
    }
    if (!status) return <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">No Subscription</span>
    const statusLower = status.toLowerCase()
    if (statusLower === "active") {
      return <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">Active</span>
    }
    if (statusLower === "trialing") {
      return <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">Trial</span>
    }
    if (statusLower === "canceled" || statusLower === "cancelled") {
      return <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">Canceled</span>
    }
    return <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">{status}</span>
  }

  const handleToggleManuallyPaid = async (e: React.MouseEvent, orgId: number, currentStatus: boolean) => {
    e.stopPropagation() // Prevent row click from triggering
    try {
      await setManuallyPaidStatus({
        organizationId: orgId,
        isManuallyPaid: !currentStatus,
      }).unwrap()
      refetch() // Refresh the data
    } catch (error) {
      console.error("Error toggling manually paid status:", error)
    }
  }

  const totalStats = useMemo(() => {
    return {
      totalOrgs: reports.length,
      totalUsers: reports.reduce((sum, org) => sum + org.userCount, 0),
      totalActive: reports.reduce((sum, org) => sum + org.activeSimulations, 0),
      totalCompleted: reports.reduce((sum, org) => sum + org.completedSimulations, 0),
    }
  }, [reports])

  return (
    <div className="flex bg-[#F9FAFB] min-h-screen w-full overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <HeaderSection />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BuildingIcon size={24} className="text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Organization Reports
                  </h1>
                  <p className="text-gray-500">
                    View all organizations and their usage statistics. Click on an organization to see user details.
                  </p>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BuildingIcon size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalStats.totalOrgs}</p>
                    <p className="text-sm text-gray-500">Organizations</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <UsersIcon size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalStats.totalUsers}</p>
                    <p className="text-sm text-gray-500">Total Users</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-100 rounded-lg">
                    <ActivityIcon size={20} className="text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalStats.totalActive}</p>
                    <p className="text-sm text-gray-500">Active Sims</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckIcon size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{totalStats.totalCompleted}</p>
                    <p className="text-sm text-gray-500">Completed Sims</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search organizations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {/* Archive Filter Tabs */}
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setArchiveFilter("active")}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      archiveFilter === "active"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => setArchiveFilter("archived")}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      archiveFilter === "archived"
                        ? "bg-white text-yellow-600 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    Archived
                  </button>
                  <button
                    onClick={() => setArchiveFilter("all")}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      archiveFilter === "all"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    All
                  </button>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {isLoading ? (
                <div className="p-8">
                  <div className="space-y-4">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-12 bg-gray-200 rounded animate-pulse"></div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th
                          onClick={() => handleSort("organizationName")}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          Organization {sortField === "organizationName" && (sortDirection === "asc" ? "↑" : "↓")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th
                          onClick={() => handleSort("userCount")}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          Users {sortField === "userCount" && (sortDirection === "asc" ? "↑" : "↓")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Active Sims
                        </th>
                        <th
                          onClick={() => handleSort("completedSimulations")}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        >
                          Completed {sortField === "completedSimulations" && (sortDirection === "asc" ? "↑" : "↓")}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Team Sims
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Manual Paid
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredAndSortedReports.map((org) => (
                        <tr
                          key={org.id}
                          onClick={() => handleOrgClick(org.id)}
                          className="hover:bg-blue-50 cursor-pointer transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <BuildingIcon size={16} className="text-gray-400" />
                              <span className="font-medium text-blue-600 hover:text-blue-800">{org.organizationName}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(org.subscriptionStatus, org.isManuallyPaid)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-gray-700">
                              <UsersIcon size={14} />
                              {org.userCount}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-cyan-600">
                              <ActivityIcon size={14} />
                              {org.activeSimulations}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckIcon size={14} />
                              {org.completedSimulations}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-purple-600">
                              <GroupsIcon size={14} />
                              {org.teamSimulations}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {org.createdOn ? new Date(org.createdOn).toLocaleDateString() : "-"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={(e) => handleToggleManuallyPaid(e, org.id, org.isManuallyPaid)}
                              disabled={isSettingManuallyPaid}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                                org.isManuallyPaid ? "bg-purple-600" : "bg-gray-200"
                              } ${isSettingManuallyPaid ? "opacity-50 cursor-not-allowed" : ""}`}
                              title={org.isManuallyPaid ? "Click to disable manual paid status" : "Click to enable manual paid status"}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  org.isManuallyPaid ? "translate-x-6" : "translate-x-1"
                                }`}
                              />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredAndSortedReports.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                      No organizations found matching your search.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Organization Details Modal */}
      {selectedOrgId !== null && orgDetailsData?.data && (
        <OrgDetailsPanel
          data={orgDetailsData.data}
          onClose={handleCloseDetails}
          isLoading={orgDetailsLoading}
        />
      )}

      {/* Loading overlay when fetching org details */}
      {selectedOrgId !== null && orgDetailsLoading && !orgDetailsData?.data && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading organization details...</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrgReportsPage
